import { create } from "jss";
import rtl from "jss-rtl";
import React, { useEffect, useState, useCallback } from "react";
import UnAuthorizedComponent from "./Components/unAuthorizedComponent";
import AuthorizedComponent from "./Components/authorizedComponent";
import {
  authServiceInstance,
  applicationTitle,
  appTheme,
  objVoiceRecorder,
  whenAppInit,
  appTimer,
  apiRoot,
  authMethode,
  stylefile,
  useExternalLoginProviders,
} from "./General/globals";
import {
  Backdrop,
  CircularProgress,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import {
  HashRouter as Router,
  Switch as RouteSwitch,
  Route,
} from "react-router-dom";
import { authLogout, authLogin, showAppBusy, hideAppBusy } from "./AppOwnState";
import {
  ExecuteLayoutEventMethods,
  GetControlPropertyFromStoreOrRefData,
} from "./General/commonFunctions";
import "./Styles/animate.min.css";
import "./Styles/basic.css";
import "./Styles/responsive.css";
import {
  GPSLocation,
  DownloadFileFromState,
  ShowSnackBar,
} from "./General/globalFunctions";
import AppDialog from "./Components/Parts/AppControls/appDialog";
import AppSnackBar from "./Components/Parts/AppControls/appSnackBar";
import AppMenu from "./Components/Parts/AppControls/appMenu";
import appNotification from "./Components/Parts/AppControls/appNotification";
import { ownStore } from "./AppOwnState/ownState";
import AppBackDrop from "./Components/Parts/AppControls/appBackDrop";
import { StylesProvider, jssPreset } from "@mui/styles";
import queryString from "query-string";
import Helmet from "react-helmet";
import UserLogin from "./Components/Parts/loginPage";
import { useIsAuthenticated } from "@azure/msal-react";

const App = () => {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const [uiDirection, setUiDirection] = useState("ltr");
  const [authid, setAuthid] = useState("");
  const [authemail, setAuthemail] = useState("");
  const [loggedUID, setLoggedUID] = useState("");
  const [theme, setTheme] = useState(createTheme(appTheme));
  const [appJSS, setAppJSS] = useState(
    create({ plugins: [...jssPreset().plugins, rtl()] })
  );

  const msalAuthenticated = useIsAuthenticated();

  // --- Apply app direction + theme dynamically ---
  const applyAppOptions = useCallback(async (direction) => {
    if (appTheme) {
      appTheme.direction = direction;
      const newTheme = createTheme(appTheme);
      const newJSS = create({ plugins: [...jssPreset().plugins, rtl()] });
      document.querySelector("body").setAttribute("dir", direction);
      if (appTheme?.header)
        document
          .querySelector("meta[name=theme-color]")
          .setAttribute("content", appTheme?.header);
      setTheme(newTheme);
      setAppJSS(newJSS);
    }
  }, []);

  // --- Handle login state via global store ---
  useEffect(() => {
    GPSLocation();

    const unsubscribe = ownStore.subscribe((storeInfo) => {
      const storeDset = storeInfo.dset;

      if (storeDset === "login") {
        const loginDS = ownStore.getState("login");
        if (loginDS?.isAuthenticated !== isAuthenticated) {
          appTimer.isAuthenticated = loginDS?.isAuthenticated;
          setIsAuthenticated(loginDS?.isAuthenticated);
        }
        setShowLoginModal(loginDS?.showLoginModal || false);
      }

      if (storeDset === "_userinfo") {
        const userinfo = ownStore.getState("_userinfo");
        userInfoStateChange(userinfo);
        setLoggedUID(userinfo?.userid);
      }

      if (storeDset === "pageinfo")
        pageStateChange(ownStore.getState("pageinfo"));
      if (storeDset === "notifyinfo")
        appNotification(ownStore.getState("notifyinfo"));
      if (storeDset === "mediainfo") {
        const mediaInfo = ownStore.getState("mediainfo");
        mediaInfo && objVoiceRecorder.doAction(mediaInfo);
      }

      if (storeDset === "downloadinfo") {
        const downloadInfo = ownStore.getState("downloadinfo");
        if (downloadInfo?.open) DownloadFileFromState(downloadInfo);
      }

      if (storeDset === "dsAppTheme") {
        const dsAppTheme = ownStore.getState("dsAppTheme");
        if (dsAppTheme) {
          appTheme.palette.mode = dsAppTheme?.mode ?? "light";
          appTheme.palette.primary.main =
            dsAppTheme?.primary_color ?? "#0b3eaf";
          appTheme.palette.secondary.main =
            dsAppTheme?.secondary_color ?? "#f2f2f7";
          setTheme(createTheme(appTheme));
        }
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, applyAppOptions]);

  // --- Lifecycle (componentDidMount) ---
  useEffect(() => {
    const initApp = async () => {
      await ExecuteLayoutEventMethods(whenAppInit);

      if (useExternalLoginProviders) {
        // console.log(msalAuthenticated, "MSAL Authenticated");
      }

      const authOK = await authServiceInstance.verifyLogin();
      ownStore.dispatch(authOK?.status ? authLogin() : authLogout());

      const queryParams = queryString.parse(window.location.search);
      if (queryParams?.authtoken && queryParams?.email) {
        setAuthid(queryParams.authtoken);
        setAuthemail(queryParams.email);
        await doSignInSSO(queryParams.email, queryParams.authtoken);
      }

      setIsPageLoading(false);
      appTimer.runTimer();

      if (stylefile !== "") _loadCSS();
    };

    initApp();
  }, [msalAuthenticated]);

  const doSignInSSO = async (email, token) => {
    try {
      window.history.pushState(null, "Home", "/");
      ownStore.dispatch(showAppBusy());
      await authServiceInstance.SSOlogin(email, token);
      const authOK = await authServiceInstance.verifyLogin();
      if (authOK?.status) {
        if (authOK?.doNext) {
          ownStore.dispatch(hideAppBusy());
          try {
            const authEvents = JSON.parse(authOK.doNext);
            await ExecuteLayoutEventMethods(authEvents);
          } catch {}
          return;
        }
        authServiceInstance.continueLogin();
        return;
      }
    } catch (ex) {
      console.error(ex);
    }
    ownStore.dispatch(hideAppBusy());
    ShowSnackBar("error", "Login Failed!");
  };

  const userInfoStateChange = async (dsUserInfo) => {
    if (dsUserInfo.uidirection !== uiDirection) {
      await applyAppOptions(dsUserInfo.uidirection ?? "ltr");
      setUiDirection(dsUserInfo.uidirection);
    }
  };

  const pageStateChange = async (dsPageInfo) => {
    const layoutInfo = dsPageInfo;
    const pInfo = layoutInfo;
    await ExecuteLayoutEventMethods(pInfo?.wheninit, pInfo);
    document.title = `${applicationTitle} ${
      layoutInfo?.title
        ? " - " + GetControlPropertyFromStoreOrRefData(layoutInfo?.title)
        : ""
    }`;
    setPageInfo(pInfo);
    await ExecuteLayoutEventMethods(pInfo?.whenload, pInfo);
    appTimer.pageTimerExecs = pInfo?.whentimer;
  };

  const _loadCSS = () => {
    require("./Styles/" + stylefile);
  };

  const _getComponentToStart = (routerProps) => {
    if (!isAuthenticated && authMethode === "cookie") {
      window.location = apiRoot;
      return <></>;
    }

    const loading = ownStore.getState("isAppBusy") || false;

    return isAuthenticated ? (
      <AuthorizedComponent
        pageInfo={pageInfo}
        showLoginModal={showLoginModal}
        authEmail={loggedUID}
        uiDirection={uiDirection ?? "ltr"}
        {...routerProps}
      />
    ) : (
      !loading && <UnAuthorizedComponent {...routerProps} />
    );
  };

  if (isPageLoading)
    return (
      <Backdrop open={isPageLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  const style = GetControlPropertyFromStoreOrRefData("[customCSS]");

  return (
    <>
      {style && style.length > 0 && (
        <Helmet>
          <style id="globalstyle">{style}</style>
        </Helmet>
      )}
      <StylesProvider jss={appJSS}>
        <ThemeProvider theme={theme}>
          <AppBackDrop />
          <Router>
            <RouteSwitch>
              <Route
                exact
                path="/"
                render={(props) => _getComponentToStart(props)}
              />
              <Route
                path="/login"
                render={(props) => <UserLogin routerProps={props} />}
              />
              <Route
                path="/:permalink/:id"
                render={(props) => _getComponentToStart(props)}
              />
              <Route
                path="/:permalink"
                render={(props) => _getComponentToStart(props)}
              />
              <Route
                path="/:permalink?authtoken=:authtoken&email=:email"
                render={(props) => _getComponentToStart(props)}
              />
            </RouteSwitch>
          </Router>
          <AppSnackBar />
          <AppDialog />
          <AppMenu />
        </ThemeProvider>
      </StylesProvider>
    </>
  );
};

export default App;
